import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMouseStore } from '../../hooks/stores/mouseStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';
import { LauncherElementType } from '../../info/launcherElementInfo';
import { DropdownMenu } from '../common/DropdownMenu/DropdownMenu';
import { stateViewsInfo } from '../../info/stateViewsInfo';
import { useScene } from '../../context/sceneContext';

export const ContextMenu = () => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [mouseData] = useMouseStore();
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [data, setData] = useState({});
  const [wasDragging, setWasDragging] = useState(false);

  const isVisible = scene.getSelectedElementsComputedVisibility() !== false;

  const getData = (
    type: LauncherElementType | 'handle' | 'scene',
    elementIDs: string[]
  ) => {
    const visibilityItems = [
      {
        separator: true,
      },
      ...(UIData.useDirectVisibility && !isVisible
        ? [
            {
              label: 'Show',
              onClick: () => {
                scene.setSelectedElementsVisibility(true, [null, null]);
                setIsOpen(false);
              },
            },
          ]
        : []),
      ...(!isVisible
        ? [
            {
              label: 'Show in Current State',
              onClick: () => {
                const stateInfo = UIData.currentStateView
                  ? stateViewsInfo[UIData.currentStateView]
                  : null;
                const significantState =
                  stateInfo?.significantLauncherState || [null, null];

                scene.setSelectedElementsVisibility(true, significantState);
                setIsOpen(false);
              },
            },
          ]
        : []),
      ...(UIData.useDirectVisibility && isVisible
        ? [
            {
              label: 'Hide',
              onClick: () => {
                scene.setSelectedElementsVisibility(false, [null, null]);
                setIsOpen(false);
              },
            },
          ]
        : []),
      ...(isVisible
        ? [
            {
              label: 'Hide in Current State',
              onClick: () => {
                const stateInfo = UIData.currentStateView
                  ? stateViewsInfo[UIData.currentStateView]
                  : null;
                const significantState =
                  stateInfo?.significantLauncherState || [null, null];

                scene.setSelectedElementsVisibility(false, significantState);
                setIsOpen(false);
              },
            },
          ]
        : []),
      ...(!UIData.useDirectVisibility
        ? [
            {
              label: 'Reset Visibilty',
              onClick: () => {
                scene.setSelectedElementsVisibility(undefined, [null, null]);
                setIsOpen(false);
              },
            },
          ]
        : []),
    ];

    const commonItems = [
      {
        label: 'Duplicate',
        onClick: () => {
          scene.duplicateSelectedElements();
          setIsOpen(false);
        },
      },
      {
        label: 'Copy',
        onClick: () => {
          scene.copySelectedElements();
          setIsOpen(false);
        },
      },
      {
        label: 'Paste',
        onClick: () => {
          scene.pasteElements();
          setIsOpen(false);
        },
      },
      ...visibilityItems,
      {
        separator: true,
      },
      {
        label: 'Delete',
        onClick: () => {
          scene.deleteSelectedElements();
          setIsOpen(false);
        },
      },
    ];

    switch (type) {
      case 'text':
      case 'link':
        return {
          items: [
            {
              label: 'Edit Text',
              onClick: () => {
                const element = elementIDs[0];
                scene.startEditingElement(element);
                setIsOpen(false);
              },
            },
            {
              separator: true,
            },
            ...commonItems,
          ],
        };
      default:
        return {
          items: [...commonItems],
        };
    }
  };

  useEffect(() => {
    if (mouseData.isDown) {
      setWasDragging(false);
    }
    if (mouseData.isDragging) {
      setWasDragging(true);
    }
  }, [mouseData.isDragging, mouseData.isDown]);

  useEffect(() => {
    if (!mouseData.isDown) {
      // Open context menu
      if (
        mouseData.button === 2 &&
        !wasDragging &&
        UIData.selectedElementIDs.length === 1
      ) {
        if (mouseData.hoveredLauncherElementType) {
          setIsOpen(true);
          setPos({ x: mouseData.pos.x, y: mouseData.pos.y });
          setData(
            getData(
              mouseData.hoveredLauncherElementType,
              UIData.selectedElementIDs
            )
          );
        }
      }
    }
    // Close context menu
    if (
      !isMouseOver &&
      isOpen &&
      (UIData.selectedElementIDs.length === 0 ||
        !mouseData.hoveredLauncherElementType ||
        mouseData.button === 0 ||
        mouseData.isDragging)
    ) {
      setIsOpen(false);
    }
  }, [
    wasDragging,
    mouseData.isDown,
    mouseData.isDragging,
    UIData.selectedElementIDs,
  ]);

  return (
    <Container
      $isOpen={isOpen}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
      style={{ left: pos.x, top: pos.y }}
    >
      <DropdownMenu data={[data]} />
    </Container>
  );
};

const Container = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  z-index: 1;

  transition: opacity 0.3s;
  ${({ $isOpen }) =>
    !$isOpen &&
    `
    opacity: 0;
    * {
      pointer-events: none;
    }
  `}
`;
